import { Color, Spacing } from '@persona-id/design-tokens';
import { AiFillIcon, XIcon } from '@persona-id/design-tokens/icons';
import { snakeCase } from 'lodash/fp';
import { useState } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import InsightPanelAdvanced from 'dashboard/components/InsightPanel/InsightPanelAdvanced';
import InsightPanelListBox from 'dashboard/components/InsightPanel/InsightPanelListBox';
import {
  INSIGHT_TYPE_TO_DESCRIPTION,
  INSIGHT_TYPE_TO_DISPLAY_NAME,
  InsightPanelForm,
  RESPONSE_TYPE_TO_DESCRIPTION,
  RESPONSE_TYPE_TO_DISPLAY_NAME,
} from 'dashboard/components/InsightPanel/util';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import {
  InsightResponseType,
  InsightType,
  MultiSelectInsightOption,
} from 'dashboard/models/document';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Button, { IconButton } from 'design-system/components/Button';
import { ErrorText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import Tooltip from 'design-system/components/Tooltip';

const MAX_MULTI_SELECT_OPTIONS = 10;

const PROMPT_EXAMPLES: Partial<InsightPanelForm>[] = [
  {
    insightType: InsightType.String,
    instruction: "Summarize the document's main points.",
  },
  {
    insightType: InsightType.MultiSelect,
    responseType: InsightResponseType.Single,
    instruction: 'From the below list, choose the primary language this document is written in.',
    options: [
      { value: 'English', description: '' },
      { value: 'Spanish', description: '' },
      { value: 'French', description: '' },
      { value: 'German', description: '' },
    ],
    // todo(danielaraujo): add defaultValue: 'Another language' when defaultValue is supported
  },
  {
    insightType: InsightType.MultiSelect,
    responseType: InsightResponseType.Multiple,
    instruction:
      "From the below list of industries, choose one or more types that best describes the company's industry in this document.",
    options: [
      { value: 'Healthcare', description: 'Healthcare industry' },
      { value: 'Technology', description: 'Technology industry' },
      { value: 'Finance', description: 'Finance industry' },
      { value: 'Retail', description: 'Retail industry' },
    ],
  },
];

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const TryPromptWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TryPromptButton = styled(Button)`
  color: ${Color.BorderIconActive};
  background-color: ${Color.CorePurple100};
`;

const PromptButton = styled(Button)`
  white-space: pre-line;
  height: auto;
  border: 1px solid ${Color.BorderHairline};
  text-align: left;

  &:hover {
    border-color: ${Color.BorderIconActive};
  }

  & > div {
    justify-content: left;
  }
`;

const ListboxOptionDescription = styled(Paragraph)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// TODO: [Documents] Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div`
  width: fit-content;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

const TooltipParagraph = styled(Paragraph)`
  width: 200px;
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

const MaximizablePanelStyles = () => css`
  width: 400px;
  top: 400px;
  min-height: unset;

  #maximizable-panel__toggle-fullscreen {
    display: none;
  }
`;

interface OptionsListBoxProps {
  formReturn: UseFormReturn<InsightPanelForm, object>;
}

const OptionsListBox = ({ formReturn }: OptionsListBoxProps) => {
  const { register, formState, watch, clearErrors, getValues, control } = formReturn;
  const { errors } = formState;

  const options = watch('options');
  const { append, remove } = useFieldArray({ control, name: 'options' });

  const addOption = () => {
    if (!getValues('options')?.length && errors?.options) {
      clearErrors('options');
    }
    append({
      value: '',
      description: '',
    });
  };

  const deleteOption = (index: number) => remove(index);

  return (
    <>
      <StyledLabel>Multiple choice options (up to {MAX_MULTI_SELECT_OPTIONS})</StyledLabel>
      <InsightPanelListBox
        formReturn={formReturn}
        options={options}
        add={addOption}
        deleteOption={deleteOption}
        disabled={options?.length >= MAX_MULTI_SELECT_OPTIONS}
        renderRow={(option: Partial<MultiSelectInsightOption>, index: number) => (
          <>
            <Paragraph
              color='primary'
              style={errors?.options?.[index] ? { color: Color.TextDanger } : {}}
            >
              {option?.value || 'Empty answer option'}
            </Paragraph>
            <ListboxOptionDescription>{option?.description}</ListboxOptionDescription>
          </>
        )}
        title='Answer option'
        panel={(index: number) => (
          <>
            <StyledLabel>Answer label</StyledLabel>
            <FieldWrapper>
              <Input
                {...register(`options.${index}.value`, {
                  setValueAs: (value: string) => value.trim(),
                })}
                placeholder='Enter answer label'
                hasError={!!errors?.options?.[index]?.value}
                autoFocus
              />
            </FieldWrapper>
            <FieldErrorText message={errors?.options?.[index]?.value?.message} />
            <Spacer height={1.5} />
            <StyledLabel>Description</StyledLabel>
            <FieldWrapper>
              <FieldWrapper>
                <TextArea
                  {...register(`options.${index}.description`)}
                  style={{ resize: 'vertical' }}
                  placeholder='Enter description'
                  hasError={!!errors?.options?.[index]?.description}
                />
              </FieldWrapper>
            </FieldWrapper>
            <FieldErrorText message={errors?.options?.[index]?.description?.message} />
            <Spacer height={1.5} />
          </>
        )}
      />
      <FieldErrorText
        message={
          errors?.options?.message ||
          (errors?.options?.length ? 'One or more answer options are invalid' : '')
        }
      />
    </>
  );
};

interface InsightPanelContentProps {
  formReturn: UseFormReturn<InsightPanelForm, object>;
  vtmplVersion: VerificationTemplateVersionDocument;
}

const InsightPanelContent = ({ formReturn, vtmplVersion }: InsightPanelContentProps) => {
  const { register, formState, watch, setValue, control } = formReturn;
  const { errors } = formState;

  const insightType = watch('insightType');
  const responseType = watch('responseType');

  const [showPromptExamples, setShowPromptExamples] = useState(false);

  const handleOpenPromptExamples = () => {
    setShowPromptExamples(true);
  };

  const handleClosePromptExamples = () => {
    setShowPromptExamples(false);
  };

  return (
    <>
      <Section>
        <StyledLabel>Insight label</StyledLabel>
        <Controller
          name='insightName'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Input
                {...field}
                placeholder='Enter insight label'
                hasError={!!errors?.insightName}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('insightKey', snakeCase(e.target.value));
                }}
                autoFocus
              />
            </FieldWrapper>
          )}
        />
        <FieldErrorText message={errors?.insightName?.message} />
        <Spacer height={1.5} />
        <StyledLabel>Insight key</StyledLabel>
        <FieldWrapper>
          <Input {...register('insightKey')} disabled hasError={!!errors?.insightKey} />
        </FieldWrapper>
        <FieldErrorText message={errors?.insightKey?.message} />
      </Section>
      <Section>
        <TryPromptWrapper>
          <Paragraph weight='bold' color='primary'>
            Configure
          </Paragraph>
          <TryPromptButton
            id='insight-panel__show-prompts'
            onClick={handleOpenPromptExamples}
            prefixIcon={<AiFillIcon />}
            width='auto'
            size='sm'
          >
            Try examples
          </TryPromptButton>
        </TryPromptWrapper>
        <Spacer height={1.5} />
        <StyledLabel>Prompt template</StyledLabel>
        <Controller
          name='insightType'
          control={control}
          render={({ field }) => (
            <FieldWrapper>
              <Select
                {...field}
                placeholder='Select prompt template'
                hasError={!!errors?.insightType}
              >
                {Object.values(InsightType).map((insightType) => (
                  <Option
                    key={insightType}
                    value={insightType}
                    subtitle={INSIGHT_TYPE_TO_DESCRIPTION[insightType]}
                  >
                    {INSIGHT_TYPE_TO_DISPLAY_NAME[insightType]}
                  </Option>
                ))}
              </Select>
            </FieldWrapper>
          )}
        />
        <FieldErrorText message={errors?.insightType?.message} />
        {insightType === InsightType.String && (
          <>
            <Spacer height={1.5} />
            <StyledLabel>
              Prompt language
              <Paragraph>
                What do you want to learn from the document? Prompt the AI to share an analysis.
              </Paragraph>
            </StyledLabel>
            <FieldWrapper>
              <TextArea
                {...register('instruction', { setValueAs: (value: string) => value.trim() })}
                style={{ resize: 'vertical' }}
                placeholder='Enter prompt language'
                hasError={!!errors?.instruction}
              />
            </FieldWrapper>
            <FieldErrorText message={errors?.instruction?.message} />
          </>
        )}
        {insightType === InsightType.MultiSelect && (
          <>
            <Spacer height={1.5} />
            <StyledLabel>Prompt response type</StyledLabel>
            <Controller
              name='responseType'
              control={control}
              render={({ field }) => (
                <FieldWrapper>
                  <Select
                    {...field}
                    placeholder='Select prompt response type'
                    hasError={!!errors?.responseType}
                  >
                    {Object.values(InsightResponseType).map((responseType) => (
                      <Option
                        key={responseType}
                        value={responseType}
                        subtitle={RESPONSE_TYPE_TO_DESCRIPTION[responseType]}
                      >
                        {RESPONSE_TYPE_TO_DISPLAY_NAME[responseType]}
                      </Option>
                    ))}
                  </Select>
                </FieldWrapper>
              )}
            />
            <FieldErrorText message={errors?.responseType?.message} />
            {responseType != null && (
              <>
                <Spacer height={1.5} />
                <StyledLabel>
                  Prompt language
                  <Paragraph>
                    What do you want to learn from the document? Prompt the AI to share an analysis.
                  </Paragraph>
                </StyledLabel>
                <FieldWrapper>
                  <TextArea
                    {...register('instruction', { setValueAs: (value: string) => value.trim() })}
                    style={{ resize: 'vertical' }}
                    placeholder='Enter prompt language'
                    hasError={!!errors?.instruction}
                  />
                </FieldWrapper>
                <FieldErrorText message={errors?.instruction?.message} />
                <Spacer height={1.5} />
                <OptionsListBox formReturn={formReturn} />
                {responseType === InsightResponseType.Single && (
                  <>
                    <Spacer height={1.5} />
                    <Tooltip
                      side='right'
                      trigger={
                        <StyledLabel>
                          <TooltipIndicatorWrapper>Default response</TooltipIndicatorWrapper>
                        </StyledLabel>
                      }
                    >
                      <TooltipParagraph>
                        This is the fallback response if none of the choice options above have
                        accurately answered the prompt.
                      </TooltipParagraph>
                    </Tooltip>
                    <FieldWrapper>
                      <Input
                        {...register('defaultResponse', {
                          setValueAs: (value: string) => value?.trim(),
                        })}
                        placeholder='Enter default response'
                      />
                    </FieldWrapper>
                  </>
                )}
              </>
            )}
          </>
        )}
        {showPromptExamples && (
          <MaximizablePanel
            title='Try prompt examples'
            offsetElementId='insight-panel__form'
            onOutsideClickAndEscPress={handleClosePromptExamples}
            getContainerStyle={MaximizablePanelStyles}
            rightButton={
              <IconButton
                id='insight-panel-list-box__close-button'
                onClick={handleClosePromptExamples}
              >
                <XIcon />
              </IconButton>
            }
          >
            {PROMPT_EXAMPLES.map((prompt, index) => (
              <>
                <PromptButton
                  key={prompt.instruction}
                  id={`insight-panel__prompt-example_${index}`}
                  onClick={() => {
                    Object.entries(prompt).forEach(([key, value]) =>
                      setValue(key as keyof InsightPanelForm, value),
                    );
                    handleClosePromptExamples();
                  }}
                  variant='inverse'
                >
                  {prompt.instruction}
                </PromptButton>
                <Spacer height={1} />
              </>
            ))}
          </MaximizablePanel>
        )}
      </Section>
      <InsightPanelAdvanced formReturn={formReturn} vtmplVersion={vtmplVersion} />
    </>
  );
};

export default InsightPanelContent;
